import { useCore } from 'contexts/core';
import useIsRtl from 'hooks/useIsRtl';
import {
  changeDoorDirection,
  disableChangeOpeningDirection,
  getCurrentDoorOpeningDirection
} from 'rangeConfig/shared/utils/doorOpeningDirection';
import { useIntl } from 'react-intl';
import Box from 'ui/Box';
import { DoorOpeningDirectionWidgetProps } from './withDoorOpeningDirection';
import InformationCircle from '@ingka/ssr-icon/paths/information-circle';
import SkapaInlineMessage, { InlineMessageVariantEnum } from 'ui/InlineMessage/SkapaInlineMessage';
import styled from 'styled-components';
import { useBehaviorTracker } from 'contexts/behaviors';
import SegmentedControl from '@ingka/segmented-control';

const InfoWrapper = styled.div`
  margin-top: ${({ theme }) => theme.space.md};
`;

const DoorOpeningDirectionWidget = ({ ...props }: DoorOpeningDirectionWidgetProps) => {
  const coreApi = useCore();
  const behaviorTracker = useBehaviorTracker();
  const isRtl = useIsRtl();
  const selectedEntity = coreApi.entities.findSelectedEntity();
  const { formatMessage } = useIntl();
  const currentDirection = getCurrentDoorOpeningDirection(selectedEntity);

  const handleSelect = () => changeDoorDirection(selectedEntity, coreApi, props);

  const options = [
    {
      label: 'storageone.left',
      connector: 'left',
      onSelect: () => handleSelect()
    },
    {
      label: 'storageone.right',
      connector: 'right',
      onSelect: () => handleSelect()
    }
  ];

  const onToggleSelect = (index: number) => {
    const option = options[index];
    if (currentDirection === option.connector) return;
    const meta = { direction: option.connector };
    behaviorTracker.withMeta(meta).custom(option.onSelect, true);
  };

  const items = isRtl
    ? options.reverse().map(option => ({
        label: formatMessage({ id: option.label }),
        icon: ''
      }))
    : options.map(option => ({
        label: formatMessage({ id: option.label }),
        icon: ''
      }));

  const selectedIndex = options.findIndex(option => option.connector === currentDirection);

  return (
    <Box mt="l" mb="l">
      <SegmentedControl
        data-testid="door-direction-toggle"
        disabled={disableChangeOpeningDirection(selectedEntity)}
        items={items}
        onClick={(_e, value) => onToggleSelect(value)}
        activeIndex={selectedIndex}
      />
      {disableChangeOpeningDirection(selectedEntity) && (
        <InfoWrapper>
          <SkapaInlineMessage
            body={formatMessage({ id: 'storageone.doorOpeningDirectionDisabledForCornerInfo' })}
            variant={InlineMessageVariantEnum.INFORMATIVE}
            ssrIcon={InformationCircle}
            subtle
          />
        </InfoWrapper>
      )}
    </Box>
  );
};

export default DoorOpeningDirectionWidget;
